import { DrawingModel, type Handle, type DrawStyle } from "../DrawingModel";
import type { Chart } from "../../chart/Chart";
import { pointInRect, withAlpha } from "../geometry";
import { labelBox, drawArrowHead } from "./helpers";

function fmtDuration(sec: number): string {
  sec = Math.abs(Math.round(sec));
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts: string[] = [];
  if (d) parts.push(d + "d");
  if (h) parts.push(h + "h");
  if (m) parts.push(m + "m");
  if (s && !d) parts.push(s + "s");
  return parts.join(" ") || "0s";
}

// Price measurement: vertical price delta between two anchors.
export class PriceRange extends DrawingModel {
  type = "pricerange";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      x2 = this.X(b, chart);
    const y1 = this.Y(a, chart),
      y2 = this.Y(b, chart);
    const lx = Math.min(x1, x2),
      w = Math.abs(x2 - x1) || 60;
    const up = b.price >= a.price;
    const col = up ? "#26a69a" : "#ef5350";
    ctx.fillStyle = withAlpha(col, 0.12);
    ctx.fillRect(lx, Math.min(y1, y2), w, Math.abs(y2 - y1));
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.strokeRect(lx, Math.min(y1, y2), w, Math.abs(y2 - y1));
    // vertical arrow
    const mx = lx + w / 2;
    ctx.beginPath();
    ctx.moveTo(mx, y1);
    ctx.lineTo(mx, y2);
    ctx.stroke();
    drawArrowHead(ctx, mx, y1, mx, y2, 8, col);
    const dPrice = b.price - a.price;
    const pct = (dPrice / a.price) * 100;
    labelBox(
      ctx,
      mx,
      Math.min(y1, y2) - 34,
      [`${dPrice >= 0 ? "+" : ""}${dPrice.toFixed(chart.priceDecimals)} (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%)`],
      { bg: col, fg: "#fff", align: "center" }
    );
  }
  handles(chart: Chart): Handle[] {
    return this.anchors.map((a, i) => ({ x: this.X(a, chart), y: this.Y(a, chart), kind: i }));
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    return pointInRect(px, py, this.X(a, chart), this.Y(a, chart), this.X(b, chart), this.Y(b, chart));
  }
}

// Date/time measurement: horizontal bar/time span between two anchors.
export class DateRange extends DrawingModel {
  type = "daterange";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      x2 = this.X(b, chart);
    const ty = this.Y(a, chart);
    const col = "#2962ff";
    ctx.fillStyle = withAlpha(col, 0.1);
    ctx.fillRect(Math.min(x1, x2), Math.min(ty, this.Y(b, chart)), Math.abs(x2 - x1), Math.abs(this.Y(b, chart) - ty) || 1);
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    const my = (ty + this.Y(b, chart)) / 2;
    ctx.beginPath();
    ctx.moveTo(x1, my);
    ctx.lineTo(x2, my);
    ctx.stroke();
    drawArrowHead(ctx, x1, my, x2, my, 8, col);
    drawArrowHead(ctx, x2, my, x1, my, 8, col);
    const bars = Math.abs(Math.round(chart.indexOfTime(b.time) - chart.indexOfTime(a.time)));
    const dur = fmtDuration(b.time - a.time);
    labelBox(ctx, (x1 + x2) / 2, my - 34, [`${bars} bars, ${dur}`], { bg: col, fg: "#fff", align: "center" });
  }
  handles(chart: Chart): Handle[] {
    return this.anchors.map((a, i) => ({ x: this.X(a, chart), y: this.Y(a, chart), kind: i }));
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    const my = (this.Y(a, chart) + this.Y(b, chart)) / 2;
    return px >= Math.min(this.X(a, chart), this.X(b, chart)) - 6 && px <= Math.max(this.X(a, chart), this.X(b, chart)) + 6 && Math.abs(py - my) <= 10;
  }
}

// Risk/Reward: anchors[0]=(t0,entry), [1]=(t1,target), [2]=(t0,stop).
export class RiskReward extends DrawingModel {
  type = "riskreward";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b, c] = this.anchors;
    const x0 = this.X(a, chart);
    const x1 = this.X(b, chart);
    const lx = Math.min(x0, x1),
      rx = Math.max(x0, x1),
      w = rx - lx;
    const yEntry = this.Y(a, chart);
    const yTarget = this.Y(b, chart);
    const yStop = this.Y(c, chart);

    // reward zone (entry -> target)
    ctx.fillStyle = withAlpha("#26a69a", 0.18);
    ctx.fillRect(lx, Math.min(yEntry, yTarget), w, Math.abs(yTarget - yEntry));
    // risk zone (entry -> stop)
    ctx.fillStyle = withAlpha("#ef5350", 0.18);
    ctx.fillRect(lx, Math.min(yEntry, yStop), w, Math.abs(yStop - yEntry));

    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#26a69a";
    ctx.strokeRect(lx, Math.min(yEntry, yTarget), w, Math.abs(yTarget - yEntry));
    ctx.strokeStyle = "#ef5350";
    ctx.strokeRect(lx, Math.min(yEntry, yStop), w, Math.abs(yStop - yEntry));

    // entry line
    ctx.strokeStyle = "#d1d4dc";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(lx, yEntry);
    ctx.lineTo(rx, yEntry);
    ctx.stroke();

    const reward = Math.abs(b.price - a.price);
    const risk = Math.abs(c.price - a.price);
    const rr = risk > 0 ? reward / risk : 0;
    const dir = b.price >= a.price ? "Long" : "Short";
    ctx.font = "11px -apple-system, system-ui, sans-serif";
    ctx.fillStyle = "#d1d4dc";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText(`Target ${reward.toFixed(chart.priceDecimals)} pts`, lx + 4, Math.min(yEntry, yTarget) + 14);
    ctx.textBaseline = "top";
    ctx.fillText(`Stop ${risk.toFixed(chart.priceDecimals)} pts`, lx + 4, Math.max(yEntry, yStop) - 14);
    labelBox(ctx, (lx + rx) / 2, yEntry - 9, [`${dir}  R:R ${rr.toFixed(2)}`], { bg: "#2962ff", fg: "#fff", align: "center" });
  }
  handles(chart: Chart): Handle[] {
    const [a, b, c] = this.anchors;
    const x0 = this.X(a, chart),
      x1 = this.X(b, chart);
    return [
      { x: x0, y: this.Y(a, chart), kind: 0 }, // entry
      { x: x1, y: this.Y(b, chart), kind: 1 }, // target
      { x: x0, y: this.Y(c, chart), kind: 2 }, // stop
      { x: x1, y: this.Y(a, chart), kind: 3 }, // width
    ];
  }
  moveHandle(kind: number, px: number, py: number, chart: Chart) {
    const [a, b, c] = this.anchors;
    const time = chart.timeOfX(px);
    const price = chart.viewport.priceOfY(py);
    if (kind === 0) {
      a.time = time;
      c.time = time;
      a.price = price;
    } else if (kind === 1) {
      b.time = time;
      b.price = price;
    } else if (kind === 2) {
      c.price = price;
    } else if (kind === 3) {
      b.time = time;
    }
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b, c] = this.anchors;
    const x0 = this.X(a, chart),
      x1 = this.X(b, chart);
    return pointInRect(px, py, x0, this.Y(c, chart), x1, this.Y(b, chart));
  }
}
